import React from 'react'
import { observer } from 'mobx-react'
import BookkeepingCompleteCycleDialogViewModel, {
  CompletedWorkItemViewModel,
} from './BookkeepingCompleteCycleDialogViewModel'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetHeaderDescription,
  ModalSheetTitle,
} from 'components/ModalSheet'
import NumericSpinnerInput from 'components/inputs/NumericSpinnerInput'
import Button from 'components/Button'
import cx from 'classnames'
import styles from './BookkeepingCompleteCycleDialog.sass'

interface BookkeepingCompleteCycleDialogProps {
  vm: BookkeepingCompleteCycleDialogViewModel
}

export default observer(function BookkeepingCompleteCycleDialog({
  vm,
}: BookkeepingCompleteCycleDialogProps) {
  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.submit.pending ? undefined : vm.close}
    >
      <ModalSheetHeader shellSize="sm" withCloseButton></ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetTitle large>Mark completed service items</ModalSheetTitle>
          <ModalSheetHeaderDescription>
            Review and adjust the amount completed during this cycle.
          </ModalSheetHeaderDescription>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock />
      </ModalSheetContent>
      <ModalSheetContent>
        <WorkItemsHeaders count={vm.itemsToComplete.length} />
        {vm.itemsToComplete.map((ci) => (
          <WorkItemRow key={ci.workItemId} vm={ci} />
        ))}
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            type="default"
            onClick={vm.close}
            disabled={vm.submit.pending}
          >
            Close
          </Button>
          <Button
            type="primary"
            dataTest="complete-cycle-submit-button"
            onClick={vm.submit}
            loading={vm.submit.pending}
            disabled={vm.submit.pending}
          >
            Complete Cycle
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface WorkItemsHeaderProps {
  count: number
}

const WorkItemsHeaders = observer(function WorkItemsHeader({
  count,
}: WorkItemsHeaderProps) {
  return (
    <div className={styles.workItemsHeadersContainer}>
      <div className={cx(styles.workItemHeader, styles.service)}>
        Services Items{' '}
        <span className={styles.serviceItemHeaderCount}>({count})</span>
      </div>
      <div className={cx(styles.workItemHeader, styles.remaining)}>
        Remaining
      </div>
      <div className={cx(styles.workItemHeader, styles.completed)}>
        Completed this cycle
      </div>
    </div>
  )
})

interface WorkItemRowProps {
  vm: CompletedWorkItemViewModel
}

const WorkItemRow = observer(function WorkItemRow({ vm }: WorkItemRowProps) {
  return (
    <div className={styles.workItemRow}>
      <div
        className={cx(
          styles.workItemCell,
          styles.service,
          styles.serviceContainer,
        )}
      >
        <div className={styles.serviceName}>{vm.inventoryItemName}</div>
        <div className={styles.serviceDescription}>
          {vm.inventoryItemDescription}
        </div>
      </div>
      <div className={cx(styles.workItemCell, styles.remaining)}>
        {vm.remaining}
      </div>
      <div className={cx(styles.workItemCell, styles.completed)}>
        <NumericSpinnerInput
          min={vm.fieldLengths.completedItems.min}
          max={vm.remaining}
          value={vm.completedItems}
          invalid={Boolean(vm.validation.getError('completedItems'))}
          onChange={(val) => vm.setCompletedItems(val)}
          dataTest={`complete-cycle-work-item-input-${vm.inventoryItemName}`}
        />
      </div>
    </div>
  )
})
